import { Draft, produce } from 'immer';
import { calcInferenceSetDerivatives } from './inference/calcInferenceSetDerivatives';
import { calcCarePhaseCostDerivatives } from './carePhases/calcCarePhaseCostDerivatives';
import { calcSupportProviderDerivatives } from './supportProviders/calcSupportProviderDerivatives';
import { calcSurveyDerivatives } from './survey/calcSurveyDerivatives';
import { fullName } from '../fullName';
import { calcCareEnvironmentsDerivatives } from './careEnvironments/calcCareEnvironmentsDerivatives';
import { calcFundingSourceDerivatives } from './funding/calcFundingSourceDerivatives';
import { applyDiscountsToCareEnvironmentCosts } from '../applyDiscountsToCareEnvironmentCosts';
import { evaluateClientInsights } from './insights/evaluateClientInsights';
import { cloneDeep } from 'lodash';

// Options that tune the derivative calc pipeline. Threaded down to the funding
// calc so callers can opt a client into the new claim-projection engine (the
// `claimProjectionEngine` feature flag, resolved at the call site).
export interface ClientDerivativeOptions {
  // When true, ported policies derive their legacy-shaped coverage outputs from
  // the new ClaimProjection instead of running the legacy coverage calc. Default
  // false (legacy) — safe for every existing caller.
  useClaimProjectionEngine?: boolean;
}

export function calcClientDerivatives(
  mutableClient: Draft<Client>,
  clientPartner: Client | null,
  options: ClientDerivativeOptions = {},
): Draft<Client> {
  return produce(mutableClient, draftClient => {
    if (clientPartner) {
      addMutableClientPartner(draftClient, clientPartner);
    }
    runAllClientDerivativeCalculations(draftClient, options);
  });
}

function runAllClientDerivativeCalculations(
  mutableClient: Draft<Client>,
  options: ClientDerivativeOptions,
): Draft<Client> {
  calcClientDerivativesImpl(mutableClient, options);
  return mutableClient;
}

export function calcClientDerivativesImpl(
  mutableClient: Draft<Client>,
  options: ClientDerivativeOptions = {},
): Draft<Client> {
  mutableClient.clientFullName = fullName(mutableClient);
  mutableClient.cachedTimestamp = new Date();

  // Single source-of-truth "today" for care-cost inflation. Pinned here, before
  // any phase-cost or ledger calc, so the Stage-1 phase costs and the new-engine
  // ledger anchor care-cost inflation at the SAME current year by construction
  // (P1-7: the two layers used to read different anchor years and drift by
  // `(1+i)^N` for a client added N years ago). Set once per derivative pass so a
  // single calc can't straddle a year boundary mid-pipeline.
  mutableClient.costInflationAnchorYear = new Date().getFullYear();

  calcSurveyDerivatives(mutableClient); // must be first
  calcCareEnvironmentsDerivatives(mutableClient);
  calcSupportProviderDerivatives(mutableClient, options);
  calcInferenceSetDerivatives(mutableClient);
  applyDiscountsToClient(mutableClient);
  calcCarePhaseCostDerivatives(mutableClient, options);
  calcFundingSourceDerivatives(mutableClient, options);
  mutableClient.clientInsights = evaluateClientInsights(mutableClient);

  return mutableClient;
}

/**
 * Re-applies discounts based on current funding sources.
 * This is needed for modeling the impact of potential funding sources on care environment costs.
 * Note: This is already performed on the server side in fetchClientLocalCareEnvironmentCosts.
 */
function applyDiscountsToClient(mutableClient: Draft<Client>): void {
  if (!mutableClient.clientLocalCareEnvironmentCosts) {
    return;
  }

  mutableClient.clientLocalCareEnvironmentCosts = applyDiscountsToCareEnvironmentCosts(
    mutableClient.clientLocalCareEnvironmentCosts as LocalCareEnvironmentCosts,
    mutableClient.multipleFundingSources,
  ) as Draft<LocalCareEnvironmentCosts>;
}

function addMutableClientPartner(mutableClient: Draft<Client>, clientPartner: Client): void {
  const clientPartnerCopy = cloneDeep(clientPartner);
  mutableClient.mutableClientPartner = clientPartnerCopy;
}
